// Event-log text for `milestone:reached`. Pure; formatEventPayload delegates
// here. Every milestone but the two champion ones (championEventText.ts) lands
// here, so the log reads "City grew — Player 3 — now a town" instead of the raw
// event type.

import { MILESTONE_IDS, isMilestonePayload } from '@aod/protocol';
import type { MessageKey, Translate } from '../../i18n/index.js';
import type { FormattableEvent } from './formatEventPayload.js';

type Payload = Record<string, unknown> | undefined;

export function milestoneLabelKey(payload: Payload): MessageKey {
  const id = payload?.milestoneId;
  // The champion pair has its own strings (championEventText.ts).
  if (id === 'legend.summoned') return 'event.legendSummoned';
  if (id === 'legend.fallen') return 'event.legendFallen';
  return (
    typeof id === 'string' && (MILESTONE_IDS as readonly string[]).includes(id)
      ? `event.milestone.${id}`
      : 'event.milestone.unknown'
  ) as MessageKey;
}

export function milestoneDetail(t: Translate, payload: Payload): string {
  if (!isMilestonePayload(payload)) return '';
  const player = payload.playerIndex + 1;
  const data = payload.data ?? {};
  switch (payload.milestoneId) {
    case 'city.tier_up': {
      const tier = Number(data.tier);
      return tier >= 2 && tier <= 5
        ? t('event.detail.milestoneTier', {
            player,
            tier: t(`event.milestone.tier.${tier}` as MessageKey),
          })
        : t('event.detail.milestone', { player });
    }
    case 'hero.level_milestone':
      return t('event.detail.milestoneLevel', { player, level: String(data.level ?? '?') });
    case 'era.advanced':
      return t('event.detail.milestoneEra', { player, era: String(data.era ?? '?') });
    case 'diplomacy.peace_signed':
    case 'battle.decisive':
    case 'city.capital_captured': {
      const other = Number(data.otherPlayerIndex ?? data.fromPlayerIndex);
      return Number.isFinite(other)
        ? t('event.detail.milestoneWith', { player, other: other + 1 })
        : t('event.detail.milestone', { player });
    }
    default:
      return payload.name
        ? t('event.detail.milestoneName', { player, name: payload.name })
        : t('event.detail.milestone', { player });
  }
}

/**
 * Whether the log should show `event` to `localPlayer`. A milestone belongs to
 * the player it happened to: you see your own, the ones you are the other party
 * to, and the big moments (an era, a victory). A rival's quiet progress is not
 * your news — and under fog it would tell you what you cannot see.
 */
export function visibleInLog(event: FormattableEvent, localPlayer: number): boolean {
  if (event.type !== 'milestone:reached') return true;
  const p = event.payload;
  if (!isMilestonePayload(p)) return false;
  if (p.playerIndex === localPlayer || p.tier !== 'small') return true;
  const d = p.data ?? {};
  return d.otherPlayerIndex === localPlayer || d.fromPlayerIndex === localPlayer;
}
